/** As contas do calendário, sem React.
 *
 * Grade do mês, janela de cada visão e navegação são aritmética de data, e é
 * onde um calendário erra: a semana que começa no domingo errado, o mês que
 * perde o dia 31, o "próximo" que pula fevereiro. Separado do componente
 * porque testar isso não deveria exigir renderizar tela nenhuma.
 *
 * Tudo aqui trabalha no fuso do **navegador**, de propósito: são as datas que a
 * pessoa vê e clica. O que a API devolve já vem agrupado por dia no fuso do
 * tenant, e o encontro dos dois é a chave `YYYY-MM-DD` — a mesma string dos
 * dois lados.
 */

export type Visao = "mes" | "semana" | "dia";

/** Domingo primeiro, como no modelo e como todo calendário em pt-BR. */
export const DIAS_DA_SEMANA = ["dom.", "seg.", "ter.", "qua.", "qui.", "sex.", "sáb."] as const;

export function inicioDoDia(data: Date): Date {
  const copia = new Date(data);
  copia.setHours(0, 0, 0, 0);
  return copia;
}

/** `YYYY-MM-DD` local — a chave que casa com o `date` que a API devolve.
 *
 * `toISOString().slice(0, 10)` daria o dia em **UTC**, e às 21h de Brasília já
 * seria o dia seguinte: a linha do compromisso cairia na célula errada.
 */
export function chaveDoDia(data: Date): string {
  const doisDigitos = (valor: number) => String(valor).padStart(2, "0");
  return `${String(data.getFullYear())}-${doisDigitos(data.getMonth() + 1)}-${doisDigitos(data.getDate())}`;
}

export function mesmoDia(a: Date, b: Date): boolean {
  return chaveDoDia(a) === chaveDoDia(b);
}

function somarDias(data: Date, dias: number): Date {
  const copia = new Date(data);
  copia.setDate(copia.getDate() + dias);
  return copia;
}

/** O domingo da semana em que a data cai. */
export function inicioDaSemana(data: Date): Date {
  return inicioDoDia(somarDias(data, -data.getDay()));
}

/** A grade do mês: semanas completas, incluindo as bordas dos meses vizinhos.
 *
 * O modelo mostra 26 de julho na primeira linha de agosto, e é isso que faz a
 * grade ter forma de calendário em vez de degrau. Os dias de fora ficam
 * visíveis mas apagados — quem os marca continua enxergando o compromisso.
 */
export function gradeDoMes(referencia: Date): Date[][] {
  const primeiro = new Date(referencia.getFullYear(), referencia.getMonth(), 1);
  const ultimo = new Date(referencia.getFullYear(), referencia.getMonth() + 1, 0);

  const semanas: Date[][] = [];
  let cursor = inicioDaSemana(primeiro);
  const fim = inicioDaSemana(ultimo);

  while (cursor <= fim) {
    semanas.push(Array.from({ length: 7 }, (_, dia) => somarDias(cursor, dia)));
    cursor = somarDias(cursor, 7);
  }
  return semanas;
}

export function diasDaSemana(referencia: Date): Date[] {
  const domingo = inicioDaSemana(referencia);
  return Array.from({ length: 7 }, (_, dia) => somarDias(domingo, dia));
}

/** Os dias que a visão desenha. A de dia é um dia só — e continua uma lista,
 * para que a tela tenha um caminho só de renderização. */
export function diasDaVisao(visao: Visao, referencia: Date): Date[] {
  if (visao === "dia") return [inicioDoDia(referencia)];
  if (visao === "semana") return diasDaSemana(referencia);
  return gradeDoMes(referencia).flat();
}

/** A janela a pedir à API: do primeiro dia desenhado ao fim do último.
 *
 * Do que está na tela, e não do mês de calendário: a grade de agosto mostra o
 * fim de julho, e sem eles a primeira linha nasceria vazia mesmo com
 * compromisso marcado.
 */
export function janelaDa(visao: Visao, referencia: Date): { desde: Date; ate: Date } {
  const dias = diasDaVisao(visao, referencia);
  const primeiro = dias[0] as Date;
  const ultimo = dias[dias.length - 1] as Date;
  return { desde: inicioDoDia(primeiro), ate: somarDias(inicioDoDia(ultimo), 1) };
}

/** Um passo para frente (`1`) ou para trás (`-1`), no tamanho da visão. */
export function navegar(visao: Visao, referencia: Date, passo: number): Date {
  if (visao === "dia") return somarDias(referencia, passo);
  if (visao === "semana") return somarDias(referencia, passo * 7);
  // `setMonth` num dia 31 escorregaria para o mês seguinte ao navegar sobre
  // fevereiro; o dia 1 é o que mantém a navegação previsível.
  return new Date(referencia.getFullYear(), referencia.getMonth() + passo, 1);
}

/** O que o cabeçalho mostra. Cada visão responde a uma pergunta diferente. */
export function rotuloDoPeriodo(visao: Visao, referencia: Date): string {
  if (visao === "dia") {
    return new Intl.DateTimeFormat("pt-BR", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(referencia);
  }

  if (visao === "semana") {
    const dias = diasDaSemana(referencia);
    const primeiro = dias[0] as Date;
    const ultimo = dias[6] as Date;
    const formato = new Intl.DateTimeFormat("pt-BR", { day: "numeric", month: "short" });
    return `${formato.format(primeiro)} – ${formato.format(ultimo)} de ${String(ultimo.getFullYear())}`;
  }

  return new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" }).format(referencia);
}

/** A hora do compromisso, curta: `15h`, `9h30`. */
export function horaDoCompromisso(iso: string): string {
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) return "";

  const minutos = quando.getMinutes();
  return minutos === 0
    ? `${String(quando.getHours())}h`
    : `${String(quando.getHours())}h${String(minutos).padStart(2, "0")}`;
}
